// RepoGraphView.tsx — hand-rolled centered git-graph SVG, theme-aware.
//
// Scope: the selected experiment's branches and nothing else. The graph
// takes the experiment slug and the descendants toggle as props and hands
// them to /graph, so what is drawn is exactly what the experiments pane
// asked for (requirement 4). Geometry lives in graphLayout.ts; this file is
// the renderer and the fetch.
//
// Per-commit rendering: each row is an AuthZ-style "button" — a rounded
// rect colored by the branch (palette/oklch theme-solver derived from the
// branch name) — with content
//
//     <author-initial avatar>  abc1234 | <subject> | <metric=val>, …
//
// The headline metric prefers the experiment's position-0 objective, unless
// the user pinned or typed a metric name in this card's filter.
//
// A small status pip on the left edge of the button colors green for
// commits whose metrics strictly improved vs the prior metric-bearing
// commit on the same branch, yellow for mixed, red for all-regressed.
//
// Clicking a row toggles it into the commit-overview selection rather than
// navigating: plain click replaces the selection, ctrl/cmd/shift click adds.
// Selected rows carry a body-colored ring.
//
// Branch labels sit in a header band above the newest row, joined to each
// branch's newest commit by a dotted line (graphLayout.ts places them).
//
// Zoom and pan (usePanZoom.ts): drag pans, ctrl/cmd-scroll or a pinch
// zooms about the pointer, and the footer's −/%/+ buttons zoom about the
// center (the % button resets).
//
// Dirty values (an experiment's dirty mark says they are not comparable)
// never count toward a headline: the header's "best" is the best CLEAN
// value and reads "—" when there is none, and the server's
// status pips are computed from clean values only. A row whose headline
// value is dirty shows it struck through (dirty.ts has both rules).
//
// Default visibility filter: only commits that carry metric values render;
// the lane-bones for skipped commits are inferred via the row connectors.
//
// The row and branch-label SVG live in GraphRow.tsx; the geometry lives in
// graphLayout.ts. What is left here is the fetch, the metric filter and the
// SVG scaffolding that connects lanes.
import { useEffect, useMemo, useRef, useState } from 'react';
import { unwrapJson, ui } from 'common';
import { apiClient } from '../api';
import type { CIJob, CIJobsResponse, GraphCommit, GraphResponse, MetricDef } from '../api';
import { metricColor } from './colors';
import { cleanBest, incomparable } from './dirty';
import { formatNum } from './format';
import { useThemeMode } from './theme';
import {
  DOT_R, LANE_SPACING, SPINE_W, TRUNK_X, VIEWBOX_W,
  computeLayout, type Lane,
} from './graphLayout';
import { BranchPill, CommitRow, jobLabel } from './GraphRow';
import { useLiveTick } from './live';
import { MAX_ZOOM, MIN_ZOOM, usePanZoom } from './usePanZoom';

interface Props {
  owner: string;
  name: string;
  /** Experiment slug the graph is scoped to; '' = repo-wide. */
  experiment: string;
  /** Widen the scope to descendant experiments' branches. */
  descendants: boolean;
  /** Branches to leave out: closed experiments' (closed.ts). */
  hiddenBranches: string[];
  /** Objective metric names, position-ordered: position 0 headlines a row. */
  objectives: string[];
  /** Commit shas currently in the overview selection. */
  selection: string[];
  onSelect: (sha: string, additive: boolean) => void;
  /** Bumped when dirty marks change, to refetch. */
  reloadKey: number;
  /** Bumped by the page's once-a-minute refresh: re-read quietly. */
  refreshKey?: number;
  /** May cancel CI jobs (increment.use). */
  canCancelJobs?: boolean;
}

export default function RepoGraphView({
  owner, name, experiment, descendants, hiddenBranches, objectives, selection, onSelect,
  reloadKey,
  refreshKey = 0,
  canCancelJobs = false,
}: Props) {
  const [graph, setGraph] = useState<GraphResponse | null>(null);
  const [metricDefs, setMetricDefs] = useState<MetricDef[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  // Commits whose every value (in view) is dirty compare to nothing; they
  // are hidden by default.
  const [hideIncomparable, setHideIncomparable] = useState(true);
  // Metric-name filter chips in the card header. When non-empty, the graph
  // narrows to commits whose metric set includes at least one of these
  // names — letting the user focus on a specific signal.
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(new Set());
  const [metricQuery, setMetricQuery] = useState('');
  // Pinned metrics belong to the experiment they were picked under; another
  // experiment may not carry them, which would filter its graph to nothing.
  useEffect(() => {
    setSelectedMetrics(new Set());
    setMetricQuery('');
  }, [experiment]);
  // Re-render on theme toggle so the solver picks new contrast targets.
  const mode = useThemeMode();

  // CI jobs measuring this repo's commits, polled while the tab is visible.
  // When a job that was queued or running leaves that state, its results
  // have probably landed, so the graph is re-read too.
  const jobTick = useLiveTick(true);
  const [jobs, setJobs] = useState<CIJob[]>([]);
  const [jobsReload, setJobsReload] = useState(0);
  const [graphReload, setGraphReload] = useState(0);
  const activeJobs = useRef<Set<string>>(new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/ci-jobs`);
        const body = await unwrapJson<CIJobsResponse>(res);
        if (cancelled) return;
        const list = (body.jobs ?? []).filter((j) => j.status !== 'succeeded');
        const active = new Set(list.filter((j) => j.status !== 'failed').map((j) => j.name));
        const finished = Array.from(activeJobs.current).some((n) => !active.has(n));
        activeJobs.current = active;
        setJobs(list);
        if (finished) setGraphReload((n) => n + 1);
      } catch {
        // Job dots are decoration; a failing route just shows none.
        if (!cancelled) setJobs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, name, jobTick, jobsReload]);
  const jobsBySha = useMemo(() => {
    const m = new Map<string, CIJob[]>();
    for (const j of jobs) m.set(j.commit, [...(m.get(j.commit) ?? []), j]);
    return m;
  }, [jobs]);
  // Layout only depends on how many dots each commit has.
  const jobCountsKey = Array.from(jobsBySha, ([sha, js]) => `${sha}:${js.length}`).sort().join(',');

  const [confirmCancel, confirmDialog] = ui.useConfirm();
  const [jobError, setJobError] = useState('');
  const cancelJob = async (j: CIJob) => {
    const failed = j.status === 'failed';
    const ok = await confirmCancel({
      title: failed ? 'Clear Failed Job' : 'Cancel CI Job',
      body: failed
        ? `Clear ${j.name}? ${jobLabel(j)}. Its Pods and logs are deleted with it.`
        : `Cancel ${j.name}? ${jobLabel(j)}. The Job and its Pods are deleted; nothing it has not posted yet is kept.`,
      confirmLabel: failed ? 'Clear' : 'Cancel Job',
      variant: 'danger',
    });
    if (!ok) return;
    setJobError('');
    try {
      const res = await apiClient.fetch(
        `/api/v1/repos/${owner}/${name}/ci-jobs/${encodeURIComponent(j.name)}`, { method: 'DELETE' });
      if (!res.ok) await unwrapJson<unknown>(res);
    } catch (err) {
      setJobError(err instanceof Error ? err.message : 'Failed to cancel the job');
    }
    setJobsReload((n) => n + 1);
  };

  // What the graph shows; a refetch of the same view is quiet (no
  // "Loading…", which would also reset the pan/zoom by unmounting the SVG).
  const shownKey = useRef('');
  useEffect(() => {
    let cancelled = false;
    const key = `${owner}/${name}/${experiment}/${descendants}/${reloadKey}`;
    const quiet = shownKey.current === key;
    if (!quiet) {
      setLoading(true);
      setError('');
    }
    const params = new URLSearchParams({ limit: '200' });
    if (experiment) params.set('experiment', experiment);
    if (descendants) params.set('descendants', '1');
    (async () => {
      try {
        const gRes = await apiClient.fetch(
          `/api/v1/repos/${owner}/${name}/graph?${params.toString()}`,
        );
        const g = await unwrapJson<GraphResponse>(gRes);
        if (!cancelled) {
          setGraph({ ...g, commits: g.commits ?? [] });
          setError('');
          shownKey.current = key;
        }
      } catch (err) {
        // A missing or erroring /graph degrades to the empty state below,
        // never to a blank page. A failed quiet refresh keeps the graph.
        if (!cancelled && !quiet) {
          setGraph({ commits: [], branch_tips: {} });
          setError(err instanceof Error ? err.message : 'Failed to load graph');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, name, experiment, descendants, reloadKey, graphReload, refreshKey]);

  // Metric metadata is decoration (units, directions, the filter list): a
  // failure here leaves the graph drawable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mRes = await apiClient.fetch(`/api/v1/repos/${owner}/${name}/metrics`);
        const m = await unwrapJson<{ metrics: MetricDef[] | null }>(mRes);
        // Curve metrics live on feeds, not commits — the graph can't show them.
        if (!cancelled) setMetricDefs((m.metrics ?? []).filter((d) => d.kind !== 'curve'));
      } catch {
        if (!cancelled) setMetricDefs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [owner, name]);

  // Joined so the memo below keys on the names, not a fresh array.
  const hiddenKey = hiddenBranches.join('\n');
  const layout = useMemo(() => {
    if (!graph) return null;
    // Closed experiments' branches come off every commit; a commit left on
    // no branch at all was only on those and is dropped. A commit merged
    // into an open branch keeps that branch, so it stays in the graph.
    const hidden = new Set(hiddenKey ? hiddenKey.split('\n') : []);
    const all = hidden.size === 0 ? graph.commits : graph.commits.flatMap((c) => {
      if (!c.branches || c.branches.length === 0) return [c];
      const branches = c.branches.filter((b) => !hidden.has(b));
      return branches.length === 0 ? [] : [{ ...c, branches }];
    });
    const tips = Object.fromEntries(
      Object.entries(graph.branch_tips ?? {}).filter(([b]) => !hidden.has(b)));
    // Filter to commits with metrics by default — that's the signal
    // increment exists to surface; raw commits with no metrics are available
    // via the "show all" toggle.
    const jobCounts = new Map<string, number>();
    for (const part of jobCountsKey ? jobCountsKey.split(',') : []) {
      const [sha, n] = part.split(':');
      jobCounts.set(sha, Number(n));
    }
    const visible = all.filter((c) => {
      const hasMetrics = !!c.metrics && Object.keys(c.metrics).length > 0;
      // A commit CI is measuring shows even before it has metrics.
      if (!showAll && !hasMetrics && !jobCounts.has(c.sha)) return false;
      if (hideIncomparable && !jobCounts.has(c.sha)
        && incomparable(c.metrics, c.dirty, selectedMetrics)) return false;
      if (selectedMetrics.size > 0) {
        if (!c.metrics) return false;
        for (const n of Object.keys(c.metrics)) {
          if (selectedMetrics.has(n)) return true;
        }
        return false;
      }
      return true;
    });
    return computeLayout(all, visible, mode, tips, jobCounts);
  }, [graph, mode, showAll, hideIncomparable, selectedMetrics, hiddenKey, jobCountsKey]);

  // Zoom/pan state lives above the early returns (hooks must run every
  // render). Buttons zoom about the drawing's center.
  const [svgEl, setSvgEl] = useState<SVGSVGElement | null>(null);
  const panZoom = usePanZoom(svgEl, { x: VIEWBOX_W / 2, y: (layout?.height ?? 0) / 2 });

  if (loading) return <div className="empty-state">Loading graph…</div>;
  if (!graph || !layout || layout.rows.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <strong>Graph</strong>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          <div className="empty-state">
            No {hideIncomparable ? 'comparable ' : ''}commits with metrics {experiment ? 'in this experiment' : 'yet'}.{' '}
            {graph && graph.commits.length > 0 && (
              <button
                type="button"
                className="btn btn-link p-0 align-baseline"
                onClick={() => { setShowAll(true); setHideIncomparable(false); }}
              >
                Show {graph.commits.length} commits anyway
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { lanes, rows, laneDots, shaIndex, labelGroups, fullBySha, height } = layout;

  // Walk the single-parent chain past metric-free ancestors until we hit a
  // sha that's in the visible set. Used to draw fork connectors when the
  // immediate parent of a visible commit was filtered out.
  function nearestVisibleAncestor(c: GraphCommit): { lane: Lane; y: number } | undefined {
    const seen = new Set<string>();
    let parentSha = c.parent_sha;
    while (parentSha && !seen.has(parentSha)) {
      seen.add(parentSha);
      const inView = shaIndex.get(parentSha);
      if (inView) return inView;
      const p = fullBySha.get(parentSha);
      if (!p) return undefined;
      parentSha = p.parent_sha;
    }
    return undefined;
  }

  const units: Record<string, string> = {};
  for (const d of metricDefs) units[d.name] = d.unit;

  // The metric search offers only the scoped experiment's metrics: the ones
  // its graph's commits carry plus its declared objectives, not every metric
  // the repo has ever seen. Repo-wide (no experiment) offers them all.
  const scopedNames = new Set(objectives);
  for (const c of graph!.commits) for (const n of Object.keys(c.metrics ?? {})) scopedNames.add(n);
  const searchDefs = experiment ? metricDefs.filter((d) => scopedNames.has(d.name)) : metricDefs;

  // Metrics the rows should headline, in priority order: pinned chips first,
  // then whatever the user is typing (an exact name match before substring
  // matches), then the experiment's objectives in position order — so a row
  // headlines the experiment's primary objective until the user asks for
  // something else.
  const query = metricQuery.trim().toLowerCase();
  const typedMatches = query
    ? searchDefs
      .map((d) => d.name)
      .filter((n) => n.toLowerCase().includes(query))
      .sort((a, b) => Number(b.toLowerCase() === query) - Number(a.toLowerCase() === query))
    : [];
  const preferredMetrics = [
    ...Array.from(selectedMetrics),
    ...typedMatches,
    ...objectives,
  ];

  // The metric the rows headline, named in the card header so the bare
  // number on each row reads as "this experiment's top-level metric" rather
  // than an unlabeled value. It is the first preferred metric any visible
  // row carries; with nothing pinned or typed that is the experiment's
  // position-0 objective, which children forked from it carry too. The best
  // visible value rides along so the header answers "how far has this
  // experiment and its children got" without scanning every row.
  const headline = preferredMetrics.find((n) =>
    rows.some((r) => !!r.commit.metrics && n in r.commit.metrics));
  const headlineDef = headline ? metricDefs.find((d) => d.name === headline) : undefined;
  const headlineHigher = headlineDef?.direction !== 'lower_is_better';
  // Dirty values are never candidates (dirty.ts rule 1).
  const headlineBest = headline
    ? cleanBest(
      rows
        .filter((r) => r.commit.metrics?.[headline] !== undefined)
        .map((r) => ({ value: r.commit.metrics![headline], dirty: !!r.commit.dirty?.[headline] })),
      headlineHigher,
    )
    : undefined;

  // Options fed into the common <Combobox>: every metric def we haven't
  // already pinned as a chip. Combobox handles the case-insensitive
  // substring filter + keyboard nav internally.
  const comboOptions = searchDefs
    .filter((d) => !selectedMetrics.has(d.name))
    .map((d) => ({
      value: d.name,
      label: `${d.name}  ${d.direction === 'higher_is_better' ? '↑' : '↓'}${d.unit ? ' ' + d.unit : ''}`,
    }));

  const addMetric = (n: string) => {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      next.add(n);
      return next;
    });
    setMetricQuery('');
  };
  const removeMetric = (n: string) => {
    setSelectedMetrics((prev) => {
      const next = new Set(prev);
      next.delete(n);
      return next;
    });
  };
  // Combobox onChange fires with either free-typed text or a selected
  // option's value. We treat a value matching any def as a selection.
  const onComboChange = (next: string) => {
    if (searchDefs.some((d) => d.name === next) && !selectedMetrics.has(next)) {
      addMetric(next);
    } else {
      setMetricQuery(next);
    }
  };

  const selected = new Set(selection);

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap align-items-center gap-2">
        <strong className="me-2">Graph</strong>
        <div style={{ flex: '1 1 14rem', minWidth: '14rem' }}>
          <ui.Combobox
            value={metricQuery}
            onChange={onComboChange}
            options={comboOptions}
            placeholder="Filter metrics…"
            style={{ width: '100%' }}
          />
        </div>
        {Array.from(selectedMetrics).map((n) => (
          <span
            key={n}
            className="badge d-inline-flex align-items-center gap-1"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${metricColor(n, mode)}`,
              color: metricColor(n, mode),
              fontWeight: 600,
            }}
          >
            <code style={{ color: 'inherit' }}>{n}</code>
            <button
              type="button"
              aria-label={`Remove ${n}`}
              className="btn btn-sm p-0 ms-1"
              style={{ lineHeight: 1, color: 'inherit', background: 'none', border: 'none' }}
              onClick={() => removeMetric(n)}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        {jobError && <div className="alert alert-danger">{jobError}</div>}
        {headline && (
          <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2 text-secondary">
            <span>Headline</span>
            <code style={{ color: metricColor(headline, mode), fontWeight: 700 }}>
              {headline} {headlineHigher ? '↑' : '↓'}
            </code>
            {headlineBest !== undefined ? (
              <span>
                best{' '}
                <strong style={{ color: metricColor(headline, mode) }}>
                  {formatNum(headlineBest)}{units[headline] || ''}
                </strong>
              </span>
            ) : (
              <span>best —</span>
            )}
          </div>
        )}
        <svg
          ref={setSvgEl}
          viewBox={`0 0 ${VIEWBOX_W} ${height}`}
          width="100%"
          height={height}
          preserveAspectRatio="xMidYMin meet"
          onPointerDown={panZoom.handlers.onPointerDown}
          onClickCapture={panZoom.handlers.onClickCapture}
          style={{
            display: 'block',
            fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
            color: 'var(--bs-body-color)',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          <g transform={panZoom.transform}>
          {/* Branch labels sit in a header band above the newest row. A
              dotted line runs from each block down to its branch's newest
              commit; rows are drawn later with an opaque fill, so the line
              passes behind any row it crosses. Branches that share a tip
              commit stack their pills in one block. */}
          {labelGroups.map((g) => (
            <line
              key={`label-link-${g.sha}`}
              x1={g.x}
              y1={g.bottom}
              x2={g.x}
              y2={g.tipY - DOT_R - 1}
              stroke={g.pills[0].lane.color}
              strokeWidth={2}
              strokeDasharray="2 5"
              strokeLinecap="round"
              opacity={0.8}
            />
          ))}
          {labelGroups.flatMap((g) => g.pills.map((p) => (
            <BranchPill key={`label-${p.lane.branch}`} x={g.x} y={p.y} lane={p.lane} />
          )))}

          {/* Lane spines connecting consecutive visible commits on the same
              lane. */}
          {Array.from(lanes.values()).map((lane) => {
            const dots = laneDots.get(lane.branch);
            if (!dots || dots.length < 2) return null;
            return (
              <line
                key={`spine-${lane.branch}`}
                x1={lane.x}
                y1={dots[0].y}
                x2={lane.x}
                y2={dots[dots.length - 1].y}
                stroke={lane.color}
                strokeWidth={SPINE_W}
                strokeLinecap="round"
                opacity={0.85}
              />
            );
          })}

          {/* Fork connectors. nearestVisibleAncestor walks the parent chain
              through metric-free ancestors to find where this lane joins
              another. The `parent.lane !== r.lane` predicate naturally fires
              only on the lane's oldest visible commit (middle commits have a
              same-lane parent visible above). */}
          {rows.map((r) => {
            const parent = nearestVisibleAncestor(r.commit);
            if (!parent || parent.lane.branch === r.lane.branch) return null;
            const fromX = parent.lane.x;
            const fromY = parent.y;
            const toX = r.lane.x;
            const toY = r.y;
            const midY = (fromY + toY) / 2;
            const d = `M ${fromX} ${fromY} C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${toY}`;
            return (
              <path
                key={`fork-${r.commit.sha}`}
                d={d}
                fill="none"
                stroke={r.lane.color}
                strokeWidth={2}
                opacity={0.85}
              />
            );
          })}
          {/* Fade tails: when a lane's oldest visible commit has no traceable
              parent in the visible set (deep history off the 200-commit
              window), extend the spine downward with a dashed fade so the
              branch communicates "continues into untracked history" instead
              of dangling. Skipped on the trunk lane — its origin isn't
              interesting. */}
          {rows.map((r) => {
            const dots = laneDots.get(r.lane.branch);
            if (!dots || dots[dots.length - 1].sha !== r.commit.sha) return null;
            if (nearestVisibleAncestor(r.commit)) return null;
            const isTrunk = Math.round((r.lane.x - TRUNK_X) / LANE_SPACING) === 0;
            if (isTrunk) return null;
            return (
              <line
                key={`tail-${r.commit.sha}`}
                x1={r.lane.x}
                y1={r.y + DOT_R + 2}
                x2={r.lane.x}
                y2={r.y + DOT_R + 2 + 36}
                stroke={r.lane.color}
                strokeWidth={SPINE_W}
                strokeLinecap="round"
                strokeDasharray="3 5"
                opacity={0.55}
              />
            );
          })}

          {rows.map((r) => (
            <CommitRow
              key={r.commit.sha}
              row={r}
              units={units}
              preferred={preferredMetrics}
              mode={mode}
              selected={selected.has(r.commit.sha)}
              onPick={(additive) => onSelect(r.commit.sha, additive)}
              jobs={jobsBySha.get(r.commit.sha)}
              onCancelJob={canCancelJobs ? cancelJob : undefined}
            />
          ))}
          </g>
        </svg>
      </div>
      <div className="card-footer d-flex flex-wrap align-items-center gap-3">
        <span className="text-secondary">
          {rows.length} commit{rows.length === 1 ? '' : 's'}
          {graph && graph.commits.length !== rows.length
            ? ` (of ${graph.commits.length})`
            : ''}
          {selection.length > 0 ? ` · ${selection.length} selected` : ''}
        </span>
        <span className="text-secondary">
          Click to select · ctrl/cmd/shift-click to add · drag to pan · ctrl/cmd-scroll to zoom
        </span>
        <div className="btn-group btn-group-sm" role="group" aria-label="Zoom">
          <button type="button" className="btn btn-outline-secondary" aria-label="Zoom out"
            disabled={panZoom.view.k <= MIN_ZOOM} onClick={panZoom.zoomOut}>−</button>
          <button type="button" className="btn btn-outline-secondary" aria-label="Reset zoom"
            onClick={panZoom.reset}>{Math.round(panZoom.view.k * 100)}%</button>
          <button type="button" className="btn btn-outline-secondary" aria-label="Zoom in"
            disabled={panZoom.view.k >= MAX_ZOOM} onClick={panZoom.zoomIn}>+</button>
        </div>
        <div className="form-check form-switch ms-auto m-0">
          <input
            id="graph-hide-incomparable"
            className="form-check-input"
            type="checkbox"
            checked={hideIncomparable}
            onChange={(e) => setHideIncomparable(e.target.checked)}
          />
          <label
            htmlFor="graph-hide-incomparable"
            className="form-check-label"
            style={{ opacity: 0.8 }}
            title="A commit whose every metric is covered by a dirty mark has no value comparable to the rest"
          >
            Hide incomparable commits
          </label>
        </div>
        <div className="form-check form-switch m-0">
          <input
            id="graph-show-all"
            className="form-check-input"
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          <label htmlFor="graph-show-all" className="form-check-label" style={{ opacity: 0.8 }}>
            Show commits without metrics
          </label>
        </div>
      </div>
      {confirmDialog}
    </div>
  );
}
